import React from "react";
import {
  AlignLeft,
  Calendar,
  Hash,
  Link as LinkIcon,
  Mail,
  MapPin,
  MoreHorizontal,
  Percent,
  Phone,
  Sigma,
  Star,
  ToggleRight,
  Type,
  Wand2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { KeyBadge } from "../../atoms/KeyBadge";
import { ActionMenu } from "../../molecules/ActionMenu";
import { DataStudioProfileChart } from "../../molecules/DataStudioProfileChart";
import { menus } from "../../registry";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import type {
  DataStudioColumnFormat,
  DataStudioColumnKind,
  DataStudioProfileColumn,
  DataStudioProfileRow,
} from "../../../services/dataStudio/dataStudioTables.types";
import {
  chunkColumnMetricsIntoRows,
  COLUMN_TRANSFORM_CATALOG,
  COLUMN_TYPE_CONFIG,
  resolveColumnMetrics,
} from "../../../services/dataStudio/dataStudioColumnProfile.config";
import {
  ColumnAction,
  ColumnActions,
  ColumnHeading,
  ColumnHeadingCell,
  ColumnIdentity,
  ColumnKindIcon,
  ImportantStar,
  ColumnName,
  DataCell,
  DistributionCell,
  ProfileRowsViewport,
  ProfileScroller,
  ProfileTable,
  ProfileTableStage,
  RowFeedback,
  RowLoadStatus,
  Statistic,
  StatisticLabel,
  StatisticsCell,
  StatisticsGrid,
  StatisticsRow,
  StatisticValue,
} from "./DataStudioProfileTable.styles";
import { useDataStudioColumnMenus } from "./useDataStudioColumnMenus";

const COLUMN_ICONS: Record<DataStudioColumnKind, LucideIcon> = {
  id: Hash,
  dimension: AlignLeft,
  string: Type,
  integer: Hash,
  numeric: Sigma,
  datetime: Calendar,
  geo: MapPin,
  boolean: ToggleRight,
};

/** Finer icon per `format`; falls back to `COLUMN_ICONS[kind]` when a column omits `format`. */
const COLUMN_FORMAT_ICONS: Record<DataStudioColumnFormat, LucideIcon> = {
  id: Hash,
  categorical: AlignLeft,
  text: Type,
  integer: Hash,
  numeric: Sigma,
  percentage: Percent,
  datetime: Calendar,
  boolean: ToggleRight,
  region: MapPin,
  url: LinkIcon,
  email: Mail,
  phone: Phone,
};

interface DataStudioProfileGridProps {
  tableName: string;
  columns: DataStudioProfileColumn[];
  visibleRows: DataStudioProfileRow[];
  visibleRowCount: number;
  hasMoreRows: boolean;
  isLoadingMoreRows: boolean;
  /** True while one request refreshes retained rows for newly loaded columns. */
  isRefreshingRowsForColumns?: boolean;
  /** True while an older row page is in flight; shows the inline progress line above the rows. */
  isLoadingOlderRows?: boolean;
  /** True while another older row page remains to be fetched; false shows the "first row" banner. */
  hasOlderRows?: boolean;
  rowsErrorMessage: string | null;
  onViewportScroll: React.UIEventHandler<HTMLElement>;
  /** Fired on the horizontal scroller — drives the column-dimension infinite scroll. */
  onGridScroll: React.UIEventHandler<HTMLElement>;
  /** True while another (rightward) column page remains to be fetched; false shows the "last column" banner. */
  hasMoreColumns?: boolean;
  /** True while the next (rightward) column page is in flight. */
  isLoadingMoreColumns?: boolean;
  /** True while another older (leftward) column page remains to be fetched; false shows the "first column" banner. */
  hasOlderColumns?: boolean;
  /** True while the previous (leftward) column page is in flight. */
  isLoadingOlderColumns?: boolean;
  /** True while the grid is scrolled to (or near) its leftmost column. Gates the "first column" banner so it only appears once the user is actually there. */
  isAtColumnsStart?: boolean;
  /** True while the grid is scrolled to (or near) its rightmost loaded column. Gates the "last column" banner the same way. */
  isAtColumnsEnd?: boolean;
  /** Backend's reason a column page failed. */
  columnsErrorMessage?: string | null;
  onColumnTransform?: (columnId: string, transformId: string) => void;
  onColumnMenu?: (columnId: string, actionId: string) => void;
  importantColumnIds: ReadonlySet<string>;
}

export const DataStudioProfileGrid: React.FC<DataStudioProfileGridProps> = ({
  tableName,
  columns,
  visibleRows,
  visibleRowCount,
  hasMoreRows,
  isLoadingMoreRows,
  isRefreshingRowsForColumns = false,
  isLoadingOlderRows = false,
  hasOlderRows = false,
  rowsErrorMessage,
  onViewportScroll,
  onGridScroll,
  hasMoreColumns = false,
  isLoadingMoreColumns = false,
  hasOlderColumns = false,
  isLoadingOlderColumns = false,
  isAtColumnsStart = true,
  isAtColumnsEnd = false,
  columnsErrorMessage = null,
  onColumnTransform,
  onColumnMenu,
  importantColumnIds,
}) => {
  const { openMenu, toggleColumnMenu, closeColumnMenu } =
    useDataStudioColumnMenus();
  const openColumn = openMenu
    ? columns.find((column) => column.id === openMenu.columnId)
    : undefined;
  const moreMenuItems = menus.dataStudioColumnMore.map((item) =>
    item.id === "markAsImportant" &&
    openColumn &&
    importantColumnIds.has(openColumn.id)
      ? { ...item, id: "unmarkAsImportant", label: "Unmark as important" }
      : item,
  );

  const transformPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openMenu?.kind === "transform",
    onOutsideClick: closeColumnMenu,
  });
  const morePortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openMenu?.kind === "more",
    onOutsideClick: closeColumnMenu,
  });

  /**
   * Only one banner slot sits above the whole (horizontally shared) grid, so
   * both column edges resolve to a single message rather than two
   * independent ones the way the row viewport's top/bottom banners do.
   *
   * `hasOlderColumns`/`hasMoreColumns` alone aren't enough to gate this: they
   * only report whether a page beyond what's *cached* exists, which stays
   * `false` for the entire time a user scrolls through columns that are
   * already cached (nothing has been evicted yet) — so "You're at the first
   * column" would stay pinned above the grid long after the user has
   * scrolled well past it. This banner also sits outside the horizontally
   * scrolling region, unlike the row banners, so it can't rely on scrolling
   * out of view on its own. Gating on `isAtColumnsStart`/`isAtColumnsEnd`
   * (the grid's actual scroll position) makes it disappear as soon as the
   * user scrolls away, matching how the row banners already behave.
   */
  const columnEdgeMessage =
    !hasOlderColumns && !hasMoreColumns
      ? "All columns loaded."
      : !hasOlderColumns && isAtColumnsStart
        ? "You're at the first column."
        : !hasMoreColumns && isAtColumnsEnd
          ? "You've reached the last column."
          : null;

  const noDistributions = columns.every((column) => !column.visualSpec);
  const noStatistics = columns.every(
    (column) => Object.keys(column.profilingSummary ?? {}).length === 0,
  );

  return (
    <>
      {columnsErrorMessage ? (
        <RowFeedback $variant="error" role="alert">
          {columnsErrorMessage}
        </RowFeedback>
      ) : isLoadingMoreColumns || isLoadingOlderColumns ? (
        <RowFeedback $variant="info">
          {isLoadingOlderColumns
            ? "Loading columns to the left..."
            : "Loading more columns..."}
        </RowFeedback>
      ) : columnEdgeMessage ? (
        <RowFeedback $variant="info">{columnEdgeMessage}</RowFeedback>
      ) : null}
      <ProfileScroller
        onScroll={onGridScroll}
        data-testid="data-studio-profile-grid-scroll"
      >
        <ProfileTableStage $columnCount={columns.length}>
          <ProfileTable aria-label={`${tableName} data profile`}>
            <thead>
              <tr>
                {columns.map((column) => {
                  const ColumnIcon = column.format
                    ? COLUMN_FORMAT_ICONS[column.format]
                    : COLUMN_ICONS[column.kind];
                  const typeLabel = COLUMN_TYPE_CONFIG[column.kind].label;
                  /**
                   * `undefined` for a column the backend has not scored
                   * (`data_quality_score: null`) — the strip renders neutral
                   * rather than claiming a health it does not know.
                   */
                  const qualityStatus = column.quality?.status;
                  return (
                    <ColumnHeadingCell
                      key={column.id}
                      id={`profile-column-${column.id}`}
                      scope="col"
                      $qualityStatus={qualityStatus}
                      data-quality-status={qualityStatus}
                      aria-label={
                        column.quality
                          ? `${column.name} ${typeLabel}, ${column.quality.score}% quality, ${column.quality.status}`
                          : `${column.name} ${typeLabel}`
                      }
                    >
                      <ColumnHeading>
                        <ColumnIdentity>
                          <ColumnKindIcon>
                            <ColumnIcon aria-hidden="true" />
                          </ColumnKindIcon>
                          <ColumnName>{column.name}</ColumnName>
                          {importantColumnIds.has(column.id) ? (
                            <ImportantStar
                              role="img"
                              aria-label={`${column.name} marked as important`}
                            >
                              <Star aria-hidden="true" />
                            </ImportantStar>
                          ) : null}
                        </ColumnIdentity>
                        <ColumnActions>
                          {column.keyKind ? (
                            <KeyBadge kind={column.keyKind} />
                          ) : null}
                          {column.availableActions.canTransform ? (
                            <ColumnAction
                              ref={
                                openMenu?.columnId === column.id &&
                                openMenu.kind === "transform"
                                  ? transformPortal.triggerRef
                                  : undefined
                              }
                              type="button"
                              aria-label={`Transform ${column.name}`}
                              aria-haspopup="menu"
                              aria-expanded={
                                openMenu?.columnId === column.id &&
                                openMenu.kind === "transform"
                              }
                              onClick={() =>
                                toggleColumnMenu(column.id, "transform")
                              }
                            >
                              <Wand2 aria-hidden="true" />
                            </ColumnAction>
                          ) : null}
                          {column.availableActions.canOpenMenu ? (
                            <ColumnAction
                              ref={
                                openMenu?.columnId === column.id &&
                                openMenu.kind === "more"
                                  ? morePortal.triggerRef
                                  : undefined
                              }
                              type="button"
                              aria-label={`Open ${column.name} menu`}
                              aria-haspopup="menu"
                              aria-expanded={
                                openMenu?.columnId === column.id &&
                                openMenu.kind === "more"
                              }
                              onClick={() =>
                                toggleColumnMenu(column.id, "more")
                              }
                            >
                              <MoreHorizontal aria-hidden="true" />
                            </ColumnAction>
                          ) : null}
                        </ColumnActions>
                      </ColumnHeading>
                    </ColumnHeadingCell>
                  );
                })}
              </tr>
              <tr>
                {columns.map((column) => (
                  <StatisticsCell key={column.id} $compact={noStatistics}>
                    <StatisticsGrid>
                      {chunkColumnMetricsIntoRows(
                        resolveColumnMetrics(column.profilingSummary),
                      ).map((row, rowIndex) => (
                        <StatisticsRow key={rowIndex}>
                          {row.map((metric) => (
                            <Statistic
                              key={metric.key}
                              $fill={row.length === 2}
                            >
                              <StatisticLabel>{metric.label}</StatisticLabel>
                              <StatisticValue>{metric.value}</StatisticValue>
                            </Statistic>
                          ))}
                        </StatisticsRow>
                      ))}
                    </StatisticsGrid>
                  </StatisticsCell>
                ))}
              </tr>
              <tr>
                {columns.map((column) => (
                  <DistributionCell key={column.id} $compact={noDistributions}>
                    <DataStudioProfileChart column={column} />
                  </DistributionCell>
                ))}
              </tr>
            </thead>
          </ProfileTable>
          {isRefreshingRowsForColumns ? (
            <RowFeedback $variant="info" role="status" aria-live="polite">
              Updating preview for loaded columns...
            </RowFeedback>
          ) : isLoadingOlderRows ? (
            <RowFeedback $variant="info">
              Loading older preview rows...
            </RowFeedback>
          ) : !hasOlderRows ? (
            <RowFeedback $variant="info" title="You're at the first row.">
              You're at the first row.
            </RowFeedback>
          ) : null}
          <ProfileRowsViewport
            variant="plain"
            hideScrollbar
            horizontalOverflow="hidden"
            onScroll={onViewportScroll}
            scrollRegionTestId="data-studio-profile-scroll"
          >
            <ProfileTable aria-label={`${tableName} preview rows`}>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((column) => (
                      <DataCell
                        key={column.id}
                        headers={`profile-column-${column.id}`}
                        $numeric={
                          column.kind === "integer" || column.kind === "numeric"
                        }
                      >
                        {row.values[column.id] ?? "—"}
                      </DataCell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </ProfileTable>
            <RowLoadStatus aria-live="polite">
              {isRefreshingRowsForColumns
                ? "Updating preview for loaded columns."
                : isLoadingOlderRows
                  ? "Loading older preview rows."
                  : isLoadingMoreRows
                    ? "Loading more preview rows."
                    : hasMoreRows
                      ? `${visibleRowCount} preview rows loaded. Scroll for more.`
                      : `All ${visibleRowCount} preview rows loaded.`}
            </RowLoadStatus>
          </ProfileRowsViewport>
          {rowsErrorMessage ? (
            <RowFeedback $variant="error" role="alert">
              {rowsErrorMessage}
            </RowFeedback>
          ) : isLoadingMoreRows ? (
            <RowFeedback $variant="info">
              Loading more preview rows...
            </RowFeedback>
          ) : !hasMoreRows ? (
            <RowFeedback $variant="info">All preview rows loaded.</RowFeedback>
          ) : null}
        </ProfileTableStage>
      </ProfileScroller>
      <ActionMenu
        items={openColumn ? COLUMN_TRANSFORM_CATALOG[openColumn.kind] : []}
        onAction={(transformId) => {
          if (openColumn) onColumnTransform?.(openColumn.id, transformId);
        }}
        isOpen={openMenu?.kind === "transform" && Boolean(openColumn)}
        onClose={closeColumnMenu}
        menuRef={transformPortal.menuRef}
        menuRect={transformPortal.rect}
        ariaLabel={
          openColumn ? `Transform ${openColumn.name}` : "Transform column"
        }
        align="right"
      />
      <ActionMenu
        items={moreMenuItems}
        onAction={(actionId) => {
          if (openColumn) onColumnMenu?.(openColumn.id, actionId);
        }}
        isOpen={openMenu?.kind === "more" && Boolean(openColumn)}
        onClose={closeColumnMenu}
        menuRef={morePortal.menuRef}
        menuRect={morePortal.rect}
        ariaLabel={
          openColumn ? `${openColumn.name} column menu` : "Column menu"
        }
        align="right"
      />
    </>
  );
};

export default DataStudioProfileGrid;
